/**
 * Settings Page - Account management and billing
 * 
 * Per Feb 2026 wireframes and plan:
 * - Profile section (name, email, phone, timezone)
 * - Subscription section (status, billing, trial info)
 * - Children section (subscription per child)
 * - Family section (invite family members)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Sentry } from '@/lib/sentry';
import { 
  Settings, 
  User, 
  CreditCard, 
  Users, 
  Clock, 
  Loader2,
  ExternalLink,
  AlertTriangle,
  CheckCircle2,
  Calendar,
  ChevronDown,
  Check,
  Phone,
  MessageSquare,
  Pencil,
  Save,
  Plus,
  X,
  Home,
  MapPin,
  Mail,
  UserPlus,
  Trash2,
  Lock,
  ShieldAlert,
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase/client';
import { glassStyles } from '@/lib/design';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { FEATURES } from '@/lib/feature-flags';
import { openCustomerPortal, isStripeConfigured, redirectToCheckout } from '@/lib/stripe';
import {
  loadHouseholdData as fetchHouseholdData,
  sendHouseholdInvite,
  cancelHouseholdInvite,
  type HouseholdMember,
  type HouseholdInvite,
} from '@/services/householdService';
import { 
  getChildrenWithSubscriptions, 
  getStatusLabel, 
  getDaysRemaining,
  isInTrial,
  type ChildWithSubscription,
  type Subscription,
} from '@/lib/billing';
import { TIMEZONE_GROUPS, getTimezoneLabel, getTimezoneAbbreviation } from '@/lib/timezones';
import { SUPPORTED_LANGUAGES, type LanguageCode, useTranslation } from '@/lib/i18n';
import { useClickOutside } from '@/hooks/useClickOutside';

// Status badge component - dark mode colors
const StatusBadge: React.FC<{ status: any }> = ({ status }) => {
  const colorMap: Record<string, string> = {
    active: 'bg-[#34C759]/20 text-[#34C759]',
    trialing: 'bg-[#111111]/20 text-[#1A1A1A]',
    past_due: 'bg-[#FFD60A]/20 text-[#FFD60A]',
    canceled: 'bg-black/[0.05] text-[#8E8E93]',
    incomplete: 'bg-[#FF9500]/20 text-[#FF9500]',
    none: 'bg-black/[0.05] text-[#8E8E93]',
  };
  const colors = colorMap[status || 'none'] || colorMap.none;
  return (
    <span className={`px-3 py-1 rounded-full text-sm font-medium ${colors}`}>
      {getStatusLabel(status)}
    </span>
  );
};

export const SettingsPage: React.FC = () => {
  const { user, profile, refetchProfile } = useAuth();
  const { t, setLanguage: setContextLanguage } = useTranslation();
  const [children, setChildren] = useState<ChildWithSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [showTimezoneSelector, setShowTimezoneSelector] = useState(false);
  const [selectedTimezone, setSelectedTimezone] = useState<string>(
    profile?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
  );
  const [savingTimezone, setSavingTimezone] = useState(false);
  
  // Language state - validate against SUPPORTED_LANGUAGES to handle legacy values
  const [showLanguageSelector, setShowLanguageSelector] = useState(false);
  const getValidLanguage = (lang?: string): LanguageCode => {
    if (lang && SUPPORTED_LANGUAGES.some(l => l.code === lang)) {
      return lang as LanguageCode;
    }
    return 'en'; // Fallback for removed languages (ar, ko, ru)
  };
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageCode>(
    getValidLanguage(profile?.preferred_language)
  );
  const [savingLanguage, setSavingLanguage] = useState(false);
  
  // Editable profile fields
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    name: profile?.name || '',
    last_name: profile?.last_name || '',
    email: profile?.email || user?.email || '',
    phone_sms: profile?.phone_sms || '',
    phone_whatsapp: profile?.phone_whatsapp || '',
    city: profile?.city || '',
  });
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Child management
  const [showAddChild, setShowAddChild] = useState(false);
  const [newChildName, setNewChildName] = useState('');
  const [addingChild, setAddingChild] = useState(false);
  const [subscribingChildId, setSubscribingChildId] = useState<string | null>(null);
  
  // Household management
  const [householdMembers, setHouseholdMembers] = useState<HouseholdMember[]>([]);
  const [householdInvites, setHouseholdInvites] = useState<HouseholdInvite[]>([]);
  const [householdId, setHouseholdId] = useState<string | null>(null);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);
  const [cancelingInviteId, setCancelingInviteId] = useState<string | null>(null);
  const [inviteFeedback, setInviteFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Password change
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [updatingPassword, setUpdatingPassword] = useState(false);

  // Child inline editing
  const [editingChildId, setEditingChildId] = useState<string | null>(null);
  const [editingChildName, setEditingChildName] = useState('');

  // Child deletion
  const [deletingChildId, setDeletingChildId] = useState<string | null>(null);
  const [showDeleteChildDialog, setShowDeleteChildDialog] = useState(false);
  const [childToDelete, setChildToDelete] = useState<{ id: string; name: string } | null>(null);

  // Account deletion
  const [showDeleteAccountDialog, setShowDeleteAccountDialog] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);

  const navigate = useNavigate();
  
  // Refs for click-outside dismissal
  const timezoneRef = React.useRef<HTMLDivElement>(null);
  const languageRef = React.useRef<HTMLDivElement>(null);
  useClickOutside(timezoneRef, () => setShowTimezoneSelector(false));
  useClickOutside(languageRef, () => setShowLanguageSelector(false));

  useEffect(() => {
    loadData();
  }, [profile?.id]);

  useEffect(() => {
    // Update timezone from profile when it loads
    if (profile?.timezone) {
      setSelectedTimezone(profile.timezone);
    }
    // Update language from profile when it loads (validated)
    if (profile?.preferred_language) {
      setSelectedLanguage(getValidLanguage(profile.preferred_language));
    }
    // Sync profile form fields
    if (profile) {
      setProfileForm({
        name: profile.name || '',
        last_name: profile.last_name || '',
        email: profile.email || user?.email || '',
        phone_sms: profile.phone_sms || '',
        phone_whatsapp: profile.phone_whatsapp || '',
        city: profile.city || '',
      });
    }
  }, [profile, user]);

  // Stripe checkout return handling — poll for webhook processing
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkoutStatus = params.get('checkout');
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    if (checkoutStatus === 'success') {
      window.history.replaceState({}, '', '/settings');
      let attempts = 0;
      const maxAttempts = 8;
      const pollForSubscription = async () => {
        if (cancelled) return;
        const { data: childrenData } = await getChildrenWithSubscriptions();
        if (cancelled) return;
        setChildren(childrenData);
        const hasActiveSub = childrenData.some(c =>
          c.subscription?.status === 'active' || c.subscription?.status === 'trialing'
        );
        if (hasActiveSub || attempts >= maxAttempts) {
          if (hasActiveSub) {
            toast.success(t('Subscription activated successfully!'));
          } else {
            toast(t('Subscription is processing. It may take a moment to appear.'), { icon: 'ℹ️' });
          }
          setLoading(false);
          return;
        }
        attempts++;
        timer = setTimeout(pollForSubscription, Math.min(1000 * Math.pow(2, attempts - 1), 8000));
      };
      pollForSubscription();
    } else if (checkoutStatus === 'canceled') {
      toast(t('Checkout was canceled.'), { icon: 'ℹ️' });
      window.history.replaceState({}, '', '/settings');
    }
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [t]);

  const loadData = async () => {
    if (!profile?.id) return;
    
    try {
      const { data: childrenData, error } = await getChildrenWithSubscriptions();
      if (error) {
        console.error('Error loading children:', error);
      }
      setChildren(childrenData);
      
      // Load household data via junction table
      await refreshHouseholdData();
    } catch (err) {
      console.error('Error loading settings data:', err);
    } finally {
      setLoading(false);
    }
  };

  const refreshHouseholdData = async () => {
    if (!profile?.id) return;
    const data = await fetchHouseholdData(profile.id);
    setHouseholdId(data.householdId);
    setHouseholdMembers(data.members);
    setHouseholdInvites(data.invites);
  };

  const handleTimezoneChange = async (tz: string) => {
    if (!profile?.id) return;
    
    // Capture previous value BEFORE optimistic update for rollback
    const previousTimezone = selectedTimezone;
    
    setSavingTimezone(true);
    setSelectedTimezone(tz); // Optimistic update
    setShowTimezoneSelector(false);

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ timezone: tz })
        .eq('id', profile.id);

      if (error) {
        console.error('Error saving timezone:', error);
        // Revert on Supabase error
        setSelectedTimezone(previousTimezone);
      }
    } catch (err) {
      console.error('Error:', err);
      // Revert on network failure or exception - was missing!
      setSelectedTimezone(previousTimezone);
    } finally {
      setSavingTimezone(false);
    }
  };

  const handleLanguageChange = async (lang: LanguageCode) => {
    if (!profile?.id) return;
    
    const previousLanguage = selectedLanguage;
    
    setSavingLanguage(true);
    setSelectedLanguage(lang);
    setShowLanguageSelector(false);

    const result = await setContextLanguage(lang);
    if (!result.success) {
      setSelectedLanguage(previousLanguage);
      setContextLanguage(previousLanguage);
    }
    setSavingLanguage(false);
  };

  const handleManageBilling = async () => {
    setOpeningPortal(true);
    try {
      const result = await openCustomerPortal();
      if (result.error) {
        toast.error(t(result.error));
        Sentry.captureException(new Error(result.error), {
          tags: { flow: 'billing', step: 'open_customer_portal' },
        });
      }
    } catch (err) {
      console.error('Error opening billing portal:', err);
      toast.error(t('Unable to open billing portal. Please try again.'));
      Sentry.captureException(err, {
        tags: { flow: 'billing', step: 'open_customer_portal' },
      });
    } finally {
      setOpeningPortal(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!profile?.id) return;
    
    if (!profileForm.name.trim()) {
      toast.error(t('Name cannot be empty.'));
      return;
    }
    
    setSavingProfile(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          name: profileForm.name.trim(),
          last_name: profileForm.last_name.trim() || null,
          email: profileForm.email.trim() || null,
          phone_sms: profileForm.phone_sms.trim() || null,
          phone_whatsapp: profileForm.phone_whatsapp.trim() || null,
          city: profileForm.city.trim() || null,
        })
        .eq('id', profile.id);

      if (error) {
        console.error('Error saving profile:', error);
        toast.error(t('Failed to save profile. Please try again.'));
      } else {
        setEditingProfile(false);
        toast.success(t('Profile saved successfully.'));
        await refetchProfile();
      }
    } catch (err) {
      console.error('Error:', err);
      toast.error(t('Failed to save profile. Please try again.'));
    } finally {
      setSavingProfile(false);
    }
  };

  const handleAddChild = async () => {
    if (!profile?.id || !newChildName.trim()) return;
    
    setAddingChild(true);
    try {
      const { data, error } = await supabase
        .from('children')
        .insert({
          parent_id: profile.id,
          household_id: householdId, // Link to household for shared access
          name: newChildName.trim(),
        } as any)
        .select('id, name')
        .single();
      
      if (error) throw error;
      
      // Refresh children list
      await loadData();
      setNewChildName('');
      setShowAddChild(false);
    } catch (err) {
      console.error('Error adding child:', err);
    } finally {
      setAddingChild(false);
    }
  };

  const handleInviteToHousehold = async () => {
    if (!householdId || !inviteEmail.trim() || !inviteName.trim() || !profile?.id) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail.trim())) {
      toast.error(t('Please enter a valid email address.'));
      return;
    }

    setSendingInvite(true);
    setInviteFeedback(null);
    try {
      const result = await sendHouseholdInvite(householdId, inviteEmail, inviteName);

      if (!result.success) {
        if (result.isDuplicate) {
          setInviteFeedback({ type: 'error', message: t('This email has already been invited to your household.') });
        } else {
          setInviteFeedback({ type: 'error', message: result.error || t('Failed to send invite. Please try again.') });
        }
        return;
      }

      setInviteEmail('');
      setInviteName('');
      setShowInviteForm(false);
      setInviteFeedback({
        type: 'success',
        message: result.emailSent
          ? t('Invite sent! They\'ll receive an email with instructions.')
          : t('Invite created. They\'ll be linked when they sign up with this email.'),
      });
      await refreshHouseholdData();
    } catch (err) {
      console.error('Error sending invite:', err);
      setInviteFeedback({ type: 'error', message: t('Failed to send invite. Please try again.') });
    } finally {
      setSendingInvite(false);
    }
  };

  const handleCancelInvite = async (inviteId: string) => {
    setCancelingInviteId(inviteId);
    setInviteFeedback(null);
    try {
      const result = await cancelHouseholdInvite(inviteId);
      if (!result.success) {
        setInviteFeedback({ type: 'error', message: result.error || t('Failed to cancel invite.') });
        return;
      }
      await refreshHouseholdData();
    } catch (err) {
      console.error('Error canceling invite:', err);
      setInviteFeedback({ type: 'error', message: t('Failed to cancel invite.') });
    } finally {
      setCancelingInviteId(null);
    }
  };

  const handleSubscribeChild = async (childId: string) => {
    setSubscribingChildId(childId);
    try {
      // Look up assigned package for this child via CRM linkage
      let priceId: string | undefined;
      const { data: crmChild } = await supabase
        .from('crm_children')
        .select('lead_id')
        .eq('child_id', childId)
        .maybeSingle();

      if (crmChild?.lead_id) {
        const { data: lead } = await supabase
          .from('crm_leads')
          .select('assigned_package_id')
          .eq('id', crmChild.lead_id)
          .maybeSingle();

        if (lead?.assigned_package_id) {
          const { data: pkg } = await supabase
            .from('subscription_packages')
            .select('stripe_price_id')
            .eq('id', lead.assigned_package_id)
            .maybeSingle();

          priceId = pkg?.stripe_price_id || undefined;
        }
      }

      const result = await redirectToCheckout(childId, { priceId });
      if (result.error) {
        console.error('Checkout error:', result.error);
        toast.error(t('Unable to start checkout. Please try again.'));
      }
    } catch (err) {
      console.error('Error starting checkout:', err);
      toast.error(t('Unable to start checkout. Please try again.'));
    } finally {
      setSubscribingChildId(null);
    }
  };

  // Password change handler
  const handleUpdatePassword = async () => {
    if (newPassword.length < 8) {
      toast.error(t('Password must be at least 8 characters'));
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(t('Passwords do not match'));
      return;
    }

    setUpdatingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) {
        toast.error(t('Failed to update password'));
        console.error('Password update error:', error.message);
      } else {
        toast.success(t('Password updated successfully'));
        setNewPassword('');
        setConfirmPassword('');
      }
    } catch (err) {
      toast.error(t('Failed to update password'));
      console.error('Password update error:', err);
    } finally {
      setUpdatingPassword(false);
    }
  };

  // Child edit handler
  const handleSaveChildName = async (childId: string) => {
    if (!editingChildName.trim()) return;

    try {
      const { error } = await supabase
        .from('children')
        .update({ name: editingChildName.trim() })
        .eq('id', childId);

      if (error) {
        toast.error(t('Failed to update child name'));
        console.error('Child update error:', error.message);
      } else {
        toast.success(t('Child name updated'));
        setEditingChildId(null);
        setEditingChildName('');
        await loadData();
      }
    } catch (err) {
      toast.error(t('Failed to update child name'));
      console.error('Child update error:', err);
    }
  };

  // Child delete handler
  const handleDeleteChild = async () => {
    if (!childToDelete) return;

    setDeletingChildId(childToDelete.id);
    try {
      const { error } = await supabase
        .from('children')
        .delete()
        .eq('id', childToDelete.id);

      if (error) {
        toast.error(t('Failed to remove child'));
        console.error('Child delete error:', error.message);
      } else {
        toast.success(`${childToDelete.name} ${t('has been removed')}`);
        await loadData();
      }
    } catch (err) {
      toast.error(t('Failed to remove child'));
      console.error('Child delete error:', err);
    } finally {
      setDeletingChildId(null);
      setShowDeleteChildDialog(false);
      setChildToDelete(null);
    }
  };

  // Account deletion handler
  const { signOut } = useAuth();

  const handleDeleteAccount = async () => {
    if (!profile?.id) return;

    setDeletingAccount(true);
    try {
      const { error } = await supabase.functions.invoke('delete-parent-account', {
        body: { profileId: profile.id },
      });

      if (error) {
        toast.error(t('Failed to delete account. Please try again.'));
        console.error('Account deletion error:', error.message);
      } else {
        await signOut();
        navigate('/login');
      }
    } catch (err) {
      toast.error(t('Failed to delete account. Please try again.'));
      console.error('Account deletion error:', err);
    } finally {
      setDeletingAccount(false);
      setShowDeleteAccountDialog(false);
    }
  };

  // Get the "primary" subscription for display
  const primarySubscription = children.find(c => 
    c.subscription?.status === 'active' || c.subscription?.status === 'trialing'
  )?.subscription;

  const hasAnySubscription = children.some(c => 
    c.subscription?.status === 'active' || c.subscription?.status === 'trialing'
  );

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-[#1A1A1A] mx-auto mb-3" />
          <p className="text-[#636366]">{t('Loading settings...')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-black/[0.05] rounded-xl flex items-center justify-center">
          <Settings className="w-5 h-5 text-[#636366]" />
        </div>
        <h1 className="text-2xl font-bold text-[#1C1C1E]">{t('Settings')}</h1>
      </div>

      {/* Trial Banner */}
      {primarySubscription && isInTrial(primarySubscription) && (
        <div className="p-4 bg-[#111111]/10 rounded-xl border border-[#1A1A1A]/20 flex items-start gap-3">
          <Calendar className="w-5 h-5 text-[#1A1A1A] flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-[#1C1C1E]">
              {(() => {
                const days = getDaysRemaining(primarySubscription?.trial_end || primarySubscription?.current_period_end);
                return days != null ? `${t('Trial ends in')} ${days} ${t('days')}` : t('Trial active');
              })()}
            </p>
            <p className="text-sm text-[#636366] mt-1">
              {t('Add a payment method to continue after your trial ends.')}
            </p>
          </div>
        </div>
      )}

      {/* Profile Section */}
      <section className="rounded-2xl p-6" style={glassStyles.card}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <User className="w-5 h-5 text-[#636366]" />
            <h2 className="text-lg font-semibold text-[#1C1C1E]">{t('Profile')}</h2>
          </div>
          {!editingProfile ? (
            <button
              onClick={() => setEditingProfile(true)}
              className="flex items-center gap-1.5 px-3 py-2.5 text-sm text-[#1A1A1A] hover:bg-[#111111]/10 rounded-lg transition-colors"
            >
              <Pencil className="w-3.5 h-3.5" />
              {t('Edit')}
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setEditingProfile(false);
                  setProfileForm({
                    name: profile?.name || '',
                    last_name: profile?.last_name || '',
                    email: profile?.email || user?.email || '',
                    phone_sms: profile?.phone_sms || '',
                    phone_whatsapp: profile?.phone_whatsapp || '',
                    city: profile?.city || '',
                  });
                }}
                className="px-3 py-2.5 text-sm text-[#8E8E93] hover:text-[#1C1C1E] transition-colors"
              >
                {t('Cancel')}
              </button>
              <button
                onClick={handleSaveProfile}
                disabled={savingProfile}
                className="flex items-center gap-1.5 px-3 py-2.5 text-sm text-[#34C759] hover:bg-[#34C759]/10 rounded-lg transition-colors disabled:opacity-50"
              >
                {savingProfile ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                {t('Save')}
              </button>
            </div>
          )}
        </div>
        
        <div className="space-y-4">
          {/* First Name */}
          <div>
            <label className="text-sm font-medium text-[#8E8E93]">{t('First Name')}</label>
            {editingProfile ? (
              <input
                type="text"
                value={profileForm.name}
                onChange={(e) => setProfileForm(prev => ({ ...prev, name: e.target.value }))}
                className="w-full mt-1 px-3 py-2 bg-black/[0.03] border border-black/[0.08] rounded-xl text-[#1C1C1E] placeholder-[#8E8E93] focus:outline-none focus:border-[#1A1A1A]/50 transition-colors"
                placeholder={t('First name')}
              />
            ) : (
              <p className="text-[#1C1C1E] mt-1">{profile?.name || t('Not set')}</p>
            )}
          </div>
          
          {/* Last Name */}
          <div>
            <label className="text-sm font-medium text-[#8E8E93]">{t('Last Name')}</label>
            {editingProfile ? (
              <input
                type="text"
                value={profileForm.last_name}
                onChange={(e) => setProfileForm(prev => ({ ...prev, last_name: e.target.value }))}
                className="w-full mt-1 px-3 py-2 bg-black/[0.03] border border-black/[0.08] rounded-xl text-[#1C1C1E] placeholder-[#8E8E93] focus:outline-none focus:border-[#1A1A1A]/50 transition-colors"
                placeholder={t('Last name')}
              />
            ) : (
              <p className="text-[#1C1C1E] mt-1">{profile?.last_name || t('Not set')}</p>
            )}
          </div>
          
          {/* Email */}
          <div>
            <label className="text-sm font-medium text-[#8E8E93]">{t('Email')}</label>
            {editingProfile ? (
              <input
                type="email"
                value={profileForm.email}
                onChange={(e) => setProfileForm(prev => ({ ...prev, email: e.target.value }))}
                className="w-full mt-1 px-3 py-2 bg-black/[0.03] border border-black/[0.08] rounded-xl text-[#1C1C1E] placeholder-[#8E8E93] focus:outline-none focus:border-[#1A1A1A]/50 transition-colors"
                placeholder={t('email@example.com')}
              />
            ) : (
              <p className="text-[#1C1C1E] mt-1">{profile?.email || user?.email}</p>
            )}
          </div>

          {/* Phone (SMS) */}
          <div>
            <label className="text-sm font-medium text-[#8E8E93] flex items-center gap-1.5">
              <Phone className="w-3.5 h-3.5" />
              {t('Phone (SMS)')}
            </label>
            {editingProfile ? (
              <input
                type="tel"
                value={profileForm.phone_sms}
                onChange={(e) => setProfileForm(prev => ({ ...prev, phone_sms: e.target.value }))}
                className="w-full mt-1 px-3 py-2 bg-black/[0.03] border border-black/[0.08] rounded-xl text-[#1C1C1E] placeholder-[#8E8E93] focus:outline-none focus:border-[#1A1A1A]/50 transition-colors"
                placeholder={t('+1 (555) 123-4567')}
              />
            ) : (
              <p className="text-[#1C1C1E] mt-1">{profile?.phone_sms || t('Not set')}</p>
            )}
          </div>

          {/* Phone (WhatsApp) */}
          <div>
            <label className="text-sm font-medium text-[#8E8E93] flex items-center gap-1.5">
              <MessageSquare className="w-3.5 h-3.5" />
              {t('Phone (WhatsApp)')}
            </label>
            {editingProfile ? (
              <input
                type="tel"
                value={profileForm.phone_whatsapp}
                onChange={(e) => setProfileForm(prev => ({ ...prev, phone_whatsapp: e.target.value }))}
                className="w-full mt-1 px-3 py-2 bg-black/[0.03] border border-black/[0.08] rounded-xl text-[#1C1C1E] placeholder-[#8E8E93] focus:outline-none focus:border-[#1A1A1A]/50 transition-colors"
                placeholder={t('+1 (555) 123-4567')}
              />
            ) : (
              <p className="text-[#1C1C1E] mt-1">{profile?.phone_whatsapp || t('Not set')}</p>
            )}
          </div>

          {/* City */}
          <div>
            <label className="text-sm font-medium text-[#8E8E93] flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" />
              {t('City')}
            </label>
            {editingProfile ? (
              <input
                type="text"
                value={profileForm.city}
                onChange={(e) => setProfileForm(prev => ({ ...prev, city: e.target.value }))}
                className="w-full mt-1 px-3 py-2 bg-black/[0.03] border border-black/[0.08] rounded-xl text-[#1C1C1E] placeholder-[#8E8E93] focus:outline-none focus:border-[#1A1A1A]/50 transition-colors"
                placeholder={t('e.g. New York')}
              />
            ) : (
              <p className="text-[#1C1C1E] mt-1">{profile?.city || t('Not set')}</p>
            )}
          </div>
          
          <div className="relative" ref={timezoneRef}>
            <label className="text-sm font-medium text-[#8E8E93]">{t('Time Zone')}</label>
            <button
              onClick={() => setShowTimezoneSelector(!showTimezoneSelector)}
              className="w-full mt-1 px-3 py-2 flex items-center justify-between text-left bg-black/[0.03] hover:bg-black/[0.05] rounded-xl border border-black/[0.08] transition-colors"
            >
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#8E8E93]" />
                <span className="text-[#1C1C1E]">
                  {getTimezoneLabel(selectedTimezone) || selectedTimezone}
                </span>
                <span className="text-xs text-[#8E8E93]">
                  ({getTimezoneAbbreviation(selectedTimezone)})
                </span>
              </div>
              {savingTimezone ? (
                <Loader2 className="w-4 h-4 animate-spin text-[#8E8E93]" />
              ) : (
                <ChevronDown className={`w-4 h-4 text-[#8E8E93] transition-transform ${showTimezoneSelector ? 'rotate-180' : ''}`} />
              )}
            </button>

            {/* Timezone Dropdown - dark theme */}
            {showTimezoneSelector && (
              <div className="absolute z-50 mt-2 w-full max-h-80 overflow-y-auto rounded-xl glass-elevated border border-black/[0.08] shadow-lg">
                {TIMEZONE_GROUPS.map(group => (
                  <div key={group.region}>
                    <div className="px-3 py-2 bg-black/[0.03] text-xs font-semibold text-[#8E8E93] uppercase tracking-wider sticky top-0">
                      {group.region}
                    </div>
                    {group.timezones.map(tz => (
                      <button
                        key={tz.value}
                        onClick={() => handleTimezoneChange(tz.value)}
                        className={`w-full px-3 py-2 flex items-center justify-between text-left hover:bg-[#111111]/10 transition-colors ${
                          selectedTimezone === tz.value ? 'bg-[#111111]/15 text-[#1A1A1A]' : 'text-[#1C1C1E]'
                        }`}
                      >
                        <span className="text-sm">{tz.label}</span>
                        {selectedTimezone === tz.value && (
                          <Check className="w-4 h-4 text-[#1A1A1A]" />
                        )}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Language Selector */}
          <div className="relative" ref={languageRef}>
            <label className="text-sm font-medium text-[#8E8E93]">{t('Language')}</label>
            <button
              onClick={() => setShowLanguageSelector(!showLanguageSelector)}
              className="w-full mt-1 px-3 py-2 flex items-center justify-between text-left bg-black/[0.03] hover:bg-black/[0.05] rounded-xl border border-black/[0.08] transition-colors"
            >
              <div className="flex items-center gap-2">
                <span className="text-lg">
                  {SUPPORTED_LANGUAGES.find(l => l.code === selectedLanguage)?.flag || '🌐'}
                </span>
                <span className="text-[#1C1C1E]">
                  {SUPPORTED_LANGUAGES.find(l => l.code === selectedLanguage)?.nativeName || 'English'}
                </span>
              </div>
              {savingLanguage ? (
                <Loader2 className="w-4 h-4 animate-spin text-[#8E8E93]" />
              ) : (
                <ChevronDown className={`w-4 h-4 text-[#8E8E93] transition-transform ${showLanguageSelector ? 'rotate-180' : ''}`} />
              )}
            </button>

            {/* Language Dropdown - opens upward on mobile to avoid being cut off */}
            {showLanguageSelector && (
              <div className="absolute z-50 bottom-full mb-2 w-full max-h-64 overflow-y-auto rounded-xl glass-elevated border border-black/[0.08] shadow-lg">
                {SUPPORTED_LANGUAGES.map(lang => (
                  <button
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                    className={`w-full px-3 py-2 flex items-center justify-between text-left hover:bg-[#111111]/10 transition-colors ${
                      selectedLanguage === lang.code ? 'bg-[#111111]/15 text-[#1A1A1A]' : 'text-[#1C1C1E]'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{lang.flag}</span>
                      <span className="text-sm">{lang.nativeName}</span>
                    </div>
                    {selectedLanguage === lang.code && (
                      <Check className="w-4 h-4 text-[#1A1A1A]" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="rounded-2xl p-6" style={glassStyles.card}>
        <div className="flex items-center gap-3 mb-4">
          <Lock className="w-5 h-5 text-[#636366]" />
          <h2 className="text-lg font-semibold text-[#1C1C1E]">{t('Security')}</h2>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-[#8E8E93]">{t('New Password')}</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full mt-1 px-3 py-2 bg-black/[0.03] border border-black/[0.08] rounded-xl text-[#1C1C1E] placeholder-[#8E8E93] focus:outline-none focus:border-[#1A1A1A]/50 transition-colors"
              placeholder={t('Minimum 8 characters')}
              autoComplete="new-password"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-[#8E8E93]">{t('Confirm Password')}</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full mt-1 px-3 py-2 bg-black/[0.03] border border-black/[0.08] rounded-xl text-[#1C1C1E] placeholder-[#8E8E93] focus:outline-none focus:border-[#1A1A1A]/50 transition-colors"
              placeholder={t('Re-enter new password')}
              autoComplete="new-password"
              onKeyDown={(e) => e.key === 'Enter' && newPassword.length >= 8 && newPassword === confirmPassword && handleUpdatePassword()}
            />
            {confirmPassword && newPassword !== confirmPassword && (
              <p className="mt-1 text-xs text-[#FF3B30]">{t('Passwords do not match')}</p>
            )}
            {newPassword && newPassword.length < 8 && (
              <p className="mt-1 text-xs text-[#FF9500]">{t('Must be at least 8 characters')}</p>
            )}
          </div>
          <button
            onClick={handleUpdatePassword}
            disabled={updatingPassword || !newPassword || newPassword.length < 8 || newPassword !== confirmPassword}
            className="flex items-center gap-2 px-4 py-2 bg-[#111111] hover:brightness-110 disabled:opacity-50 text-[#1A1A1A] text-sm font-medium rounded-xl transition-all"
          >
            {updatingPassword ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
            {t('Update Password')}
          </button>
        </div>
      </section>

      {/* Subscription Section */}
      <section className="rounded-2xl p-6" style={glassStyles.card}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <CreditCard className="w-5 h-5 text-[#636366]" />
            <h2 className="text-lg font-semibold text-[#1C1C1E]">{t('Manage Billing')}</h2>
          </div>
          
          {isStripeConfigured() && hasAnySubscription && (
            <button
              onClick={handleManageBilling}
              disabled={openingPortal}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#1A1A1A] hover:bg-[#111111]/10 rounded-lg transition-colors disabled:opacity-50"
            >
              {openingPortal ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ExternalLink className="w-4 h-4" />
              )}
              {t('Manage Billing')}
            </button>
          )}
        </div>

        {!hasAnySubscription ? (
          <div className="text-center py-6">
            <div className="w-12 h-12 bg-black/[0.05] rounded-full flex items-center justify-center mx-auto mb-3">
              <CreditCard className="w-6 h-6 text-[#8E8E93]" />
            </div>
            <p className="text-[#1C1C1E] mb-2">{t('No active subscription')}</p>
            <p className="text-sm text-[#8E8E93]">
              {t('Contact us to start your Turn journey.')}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Current Plan */}
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-[#8E8E93]">{t('Current Plan')}</label>
                <p className="text-[#1C1C1E] font-medium">{t('Turn Platform')}</p>
              </div>
              {primarySubscription && (
                <StatusBadge status={primarySubscription.status} />
              )}
            </div>

            {/* Next Billing */}
            {primarySubscription?.current_period_end && (
              <div>
                <label className="text-sm font-medium text-[#8E8E93]">
                  {isInTrial(primarySubscription) ? t('Trial Ends') : t('Next Billing')}
                </label>
                <p className="text-[#1C1C1E]">
                  {new Date(primarySubscription.current_period_end).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </p>
              </div>
            )}

            {/* Pricing */}
            <div>
              <label className="text-sm font-medium text-[#8E8E93]">{t('Pricing')}</label>
              <p className="text-[#1C1C1E]">{t('Live Sessions: $60/session · On-Demand: $0.25/min')}</p>
            </div>
          </div>
        )}
      </section>

      {/* Children Section */}
      <section className="rounded-2xl p-6" style={glassStyles.card}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Users className="w-5 h-5 text-[#636366]" />
            <h2 className="text-lg font-semibold text-[#1C1C1E]">{t('Children')}</h2>
          </div>
          {!showAddChild && (
            <button
              onClick={() => setShowAddChild(true)}
              className="flex items-center gap-1.5 px-3 py-2.5 text-sm text-[#1A1A1A] hover:bg-[#111111]/10 rounded-lg transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              {t('Add Child')}
            </button>
          )}
        </div>

        {/* Add Child Form */}
        {showAddChild && (
          <div className="mb-4 p-4 bg-black/[0.03] rounded-xl border border-black/[0.08]">
            <div className="flex items-center gap-3">
              <input
                type="text"
                value={newChildName}
                onChange={e => setNewChildName(e.target.value)}
                placeholder={t("Child's first name")}
                className="flex-1 px-3 py-2 bg-black/[0.03] border border-black/[0.08] rounded-xl text-[#1C1C1E] placeholder-[#8E8E93] focus:outline-none focus:border-[#1A1A1A]/50 transition-colors"
                autoFocus
                onKeyDown={e => e.key === 'Enter' && newChildName.trim() && handleAddChild()}
              />
              <button
                onClick={handleAddChild}
                disabled={!newChildName.trim() || addingChild}
                className="px-4 py-2 bg-[#111111] hover:brightness-110 disabled:opacity-50 text-[#1A1A1A] text-sm font-medium rounded-xl transition-all"
              >
                {addingChild ? <Loader2 className="w-4 h-4 animate-spin" /> : t('Add')}
              </button>
              <button
                onClick={() => { setShowAddChild(false); setNewChildName(''); }}
                className="p-2 text-[#8E8E93] hover:text-[#1C1C1E] transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="mt-2 text-xs text-[#C7C7CC]">
              {t('First name only. You can subscribe this child after adding.')}
            </p>
          </div>
        )}

        {children.length === 0 && !showAddChild ? (
          <p className="text-[#8E8E93] text-center py-4">{t('No children added yet.')}</p>
        ) : (
          <div className="space-y-3">
            {children.map(child => {
              const isSubscribed = child.subscription_status === 'active' || child.subscription_status === 'trialing';
              const isPastDue = child.subscription_status === 'past_due';
              const needsSubscription = !isSubscribed && !isPastDue;
              const isEditing = editingChildId === child.id;
              
              return (
                <div 
                  key={child.id}
                  className="flex items-center justify-between p-3 bg-black/[0.03] rounded-xl border border-black/[0.08]"
                >
                  <div className="flex-1 min-w-0">
                    {isEditing ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={editingChildName}
                          onChange={(e) => setEditingChildName(e.target.value)}
                          className="flex-1 px-2 py-1 bg-black/[0.03] border border-black/[0.08] rounded-lg text-[#1C1C1E] text-sm placeholder-[#8E8E93] focus:outline-none focus:border-[#1A1A1A]/50 transition-colors"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && editingChildName.trim()) handleSaveChildName(child.id);
                            if (e.key === 'Escape') { setEditingChildId(null); setEditingChildName(''); }
                          }}
                        />
                        <button
                          onClick={() => handleSaveChildName(child.id)}
                          disabled={!editingChildName.trim()}
                          className="p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center text-[#34C759] hover:bg-[#34C759]/10 rounded-lg transition-colors disabled:opacity-50"
                        >
                          <Save className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => { setEditingChildId(null); setEditingChildName(''); }}
                          className="p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center text-[#8E8E93] hover:text-[#1C1C1E] transition-colors"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <p className="font-medium text-[#1C1C1E]">{child.name}</p>
                        {child.subscription_status && child.subscription_status !== 'none' ? (
                          <p className="text-sm text-[#8E8E93]">
                            {getStatusLabel(child.subscription_status)}
                          </p>
                        ) : (
                          <p className="text-sm text-[#8E8E93]">{t('No subscription')}</p>
                        )}
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-2 ml-2">
                    {!isEditing && (
                      <>
                        <button
                          onClick={() => {
                            setEditingChildId(child.id);
                            setEditingChildName(child.name);
                          }}
                          className="p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center text-[#C7C7CC] hover:text-[#1A1A1A] hover:bg-[#111111]/10 rounded-lg transition-colors"
                          title={t('Edit name')}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => {
                            setChildToDelete({ id: child.id, name: child.name });
                            setShowDeleteChildDialog(true);
                          }}
                          disabled={deletingChildId === child.id}
                          className="p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center text-[#C7C7CC] hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded-lg transition-colors disabled:opacity-50"
                          title={t('Remove child')}
                        >
                          {deletingChildId === child.id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Trash2 className="w-3.5 h-3.5" />
                          )}
                        </button>
                      </>
                    )}
                    {isSubscribed && (
                      <CheckCircle2 className="w-5 h-5 text-[#34C759]" />
                    )}
                    {isPastDue && (
                      <AlertTriangle className="w-5 h-5 text-[#FFD60A]" />
                    )}
                    {needsSubscription && isStripeConfigured() && (
                      <button
                        onClick={() => handleSubscribeChild(child.id)}
                        disabled={subscribingChildId === child.id}
                        className="px-3 py-2.5 text-xs font-medium bg-[#111111] hover:brightness-110 disabled:opacity-50 text-[#1A1A1A] rounded-lg transition-all"
                      >
                        {subscribingChildId === child.id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          t('Subscribe')
                        )}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Household Section */}
      {householdId && (
        <section className="rounded-2xl p-6" style={glassStyles.card}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Home className="w-5 h-5 text-[#636366]" />
              <h2 className="text-lg font-semibold text-[#1C1C1E]">{t('Household')}</h2>
            </div>
            {!showInviteForm && (
              <button
                onClick={() => setShowInviteForm(true)}
                className="flex items-center gap-1.5 px-3 py-2.5 text-sm text-[#1A1A1A] hover:bg-[#111111]/10 rounded-lg transition-colors"
              >
                <UserPlus className="w-3.5 h-3.5" />
                {t('Invite')}
              </button>
            )}
          </div>

          <p className="text-sm text-[#8E8E93] mb-4">
            {t('Household members share access to children, scheduling, and progress.')}
          </p>

          {/* Feedback Banner */}
          {inviteFeedback && (
            <div className={`flex items-center gap-2 p-3 rounded-xl mb-4 text-sm ${
              inviteFeedback.type === 'success'
                ? 'bg-[#34C759]/10 border border-[#34C759]/20 text-[#34C759]'
                : 'bg-[#FF3B30]/10 border border-[#FF3B30]/20 text-[#FF3B30]'
            }`}>
              {inviteFeedback.type === 'success' ? (
                <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
              ) : (
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              )}
              <span>{inviteFeedback.message}</span>
              <button
                onClick={() => setInviteFeedback(null)}
                className="ml-auto p-0.5 hover:opacity-70 transition-opacity"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {/* Members List */}
          <div className="space-y-2 mb-4">
            {householdMembers.map(member => {
              const memberProfile = member.profiles as any;
              const isCurrentUser = member.profile_id === profile?.id;
              return (
                <div
                  key={member.id}
                  className="flex items-center justify-between p-3 bg-black/[0.03] rounded-xl border border-black/[0.08]"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#111111]/20 flex items-center justify-center">
                      <span className="text-sm font-bold text-[#1A1A1A]">
                        {(memberProfile?.name || '?').charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-[#1C1C1E] text-sm">
                        {memberProfile?.name || t('Unknown')}
                        {isCurrentUser && <span className="text-[#8E8E93] ml-1">{t('(you)')}</span>}
                      </p>
                      <p className="text-xs text-[#8E8E93]">
                        {memberProfile?.email || ''} · {member.role}
                      </p>
                    </div>
                  </div>
                  <CheckCircle2 className="w-4 h-4 text-[#34C759]" />
                </div>
              );
            })}
          </div>

          {/* Pending Invites */}
          {householdInvites.length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-medium text-[#8E8E93] uppercase tracking-wider mb-2">
                {t('Pending Invites')}
              </p>
              <div className="space-y-2">
                {householdInvites.map(invite => (
                  <div
                    key={invite.id}
                    className="flex items-center justify-between p-3 bg-black/[0.03] rounded-xl border border-dashed border-black/[0.08]"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-black/[0.05] flex items-center justify-center">
                        <Mail className="w-3.5 h-3.5 text-[#8E8E93]" />
                      </div>
                      <div>
                        <p className="font-medium text-[#636366] text-sm">{invite.name}</p>
                        <p className="text-xs text-[#8E8E93]">{invite.email}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleCancelInvite(invite.id)}
                      disabled={cancelingInviteId === invite.id}
                      className="p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center text-[#C7C7CC] hover:text-[#FF3B30] transition-colors disabled:opacity-50"
                      title={t('Cancel invite')}
                    >
                      {cancelingInviteId === invite.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="w-3.5 h-3.5" />
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Invite Form */}
          {showInviteForm && (
            <div className="p-4 bg-black/[0.03] rounded-xl border border-black/[0.08]">
              <p className="text-sm font-medium text-[#636366] mb-3">{t('Invite to household')}</p>
              <div className="space-y-3">
                <input
                  type="text"
                  value={inviteName}
                  onChange={e => setInviteName(e.target.value)}
                  placeholder={t('Name')}
                  className="w-full px-3 py-2 bg-black/[0.03] border border-black/[0.08] rounded-xl text-[#1C1C1E] placeholder-[#8E8E93] focus:outline-none focus:border-[#1A1A1A]/50 transition-colors text-sm"
                  autoFocus
                />
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={e => setInviteEmail(e.target.value)}
                  placeholder={t('Email address')}
                  className="w-full px-3 py-2 bg-black/[0.03] border border-black/[0.08] rounded-xl text-[#1C1C1E] placeholder-[#8E8E93] focus:outline-none focus:border-[#1A1A1A]/50 transition-colors text-sm"
                  onKeyDown={e => e.key === 'Enter' && inviteEmail.trim() && inviteName.trim() && handleInviteToHousehold()}
                />
                <p className="text-xs text-[#C7C7CC]">
                  {t('When they create a Turn account with this email, they\'ll automatically join your household.')}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleInviteToHousehold}
                    disabled={!inviteEmail.trim() || !inviteName.trim() || sendingInvite}
                    className="px-4 py-2 bg-[#111111] hover:brightness-110 disabled:opacity-50 text-[#1A1A1A] text-sm font-medium rounded-xl transition-all"
                  >
                    {sendingInvite ? <Loader2 className="w-4 h-4 animate-spin" /> : t('Send Invite')}
                  </button>
                  <button
                    onClick={() => { setShowInviteForm(false); setInviteEmail(''); setInviteName(''); }}
                    className="px-4 py-2 text-sm text-[#8E8E93] hover:text-[#1C1C1E] transition-colors"
                  >
                    {t('Cancel')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </section>
      )}

      {/* Stripe Not Configured Warning */}
      {!isStripeConfigured() && (
        <div className="p-4 bg-[#FFD60A]/10 rounded-xl border border-[#FFD60A]/20 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-[#FFD60A] flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-[#1C1C1E]">{t('Billing not configured')}</p>
            <p className="text-sm text-[#636366]">
              {t('Contact support for billing assistance.')}
            </p>
          </div>
        </div>
      )}

      {/* Danger Zone */}
      <section className="rounded-2xl p-6 border border-[#FF3B30]/20" style={{ ...glassStyles.card, borderColor: 'rgba(255, 69, 58, 0.2)' }}>
        <div className="flex items-center gap-3 mb-4">
          <ShieldAlert className="w-5 h-5 text-[#FF3B30]" />
          <h2 className="text-lg font-semibold text-[#FF3B30]">{t('Danger Zone')}</h2>
        </div>
        <p className="text-sm text-[#8E8E93] mb-4">
          {t('Permanently delete your account and all associated data. This action cannot be undone.')}
        </p>
        <button
          onClick={() => setShowDeleteAccountDialog(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-[#FF3B30]/10 hover:bg-[#FF3B30]/20 border border-[#FF3B30]/30 text-[#FF3B30] font-medium text-sm rounded-xl transition-colors"
        >
          <Trash2 className="w-4 h-4" />
          {t('Delete Account')}
        </button>
      </section>

      {/* Confirm Dialogs */}
      <ConfirmDialog
        isOpen={showDeleteChildDialog}
        onClose={() => { setShowDeleteChildDialog(false); setChildToDelete(null); }}
        onConfirm={handleDeleteChild}
        title={childToDelete?.name ? `${t('Remove')} ${childToDelete.name}?` : t('Remove child?')}
        message={`${t('This will permanently delete')} ${childToDelete?.name || t('this child')} ${t('and all their progress, recordings, and reading data. This cannot be undone.')}`}
        confirmLabel={t('Remove Child')}
        variant="danger"
        loading={!!deletingChildId}
      />

      <ConfirmDialog
        isOpen={showDeleteAccountDialog}
        onClose={() => setShowDeleteAccountDialog(false)}
        onConfirm={handleDeleteAccount}
        title={t('Delete Account')}
        message={t('This will permanently delete your account, all children, and all associated data. This action cannot be undone.')}
        confirmLabel={t('Delete Account')}
        variant="danger"
        loading={deletingAccount}
      />
    </div>
  );
};

export default SettingsPage;
